import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { ExpandableRichText } from '@/presentation/components/common/text/ExpandableRichText';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserUtils } from '@/domain/utils/userUtils';
import { MathUtils } from '@/domain/utils/mathUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { UserDetailsStatColumn } from './UserDetailsStatColumn';
import { UserDetailsActions } from './UserDetailsActions';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface UserDetailsHeaderProps {
  userData: UserEntity | null;
  resolvedUserId: string;
  isCurrentUser: boolean;
  subscribeLabel: string;
  onSubscribePress: () => void;
  onCommunityPress: () => void;
  onSecondaryPress: () => void;
  onAvatarPress: () => void;
}

export const UserDetailsHeader: React.FC<UserDetailsHeaderProps> = ({
  userData,
  resolvedUserId,
  isCurrentUser,
  subscribeLabel,
  onSubscribePress,
  onCommunityPress,
  onSecondaryPress,
  onAvatarPress,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const subscriptionCharge = userData?.subscriptionCharge ?? 0;
  const isFreeSubscription = subscriptionCharge === 0;
  const subscriptionCoinsLabel = !isFreeSubscription
    ? MathUtils.removeTrailingZero(MathUtils.coinsFromUSD(subscriptionCharge))
    : null;

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: `0 ${responsive.spacing.lg}px ${responsive.spacing.lg}px`,
        gap: responsive.gap.base,
        overflow: 'hidden',
      }}
    >
      <div
        className="flex items-center"
        style={{ gap: responsive.gap.lg }}
      >
        <button
          type="button"
          {...qaAttr({ id: 'user-details-avatar' })}
          onClick={onAvatarPress}
          className="rounded-full hover:opacity-80 transition-opacity focus:outline-none"
          style={{
            padding: 0,
            border: 'none',
            background: 'transparent',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          aria-label={userData?.name ?? l10n.common.emptyPlaceholder}
        >
          <CachedImage
            src={UserUtils.generateUserProfileImageUrl(resolvedUserId)}
            alt={userData?.name ?? l10n.common.emptyPlaceholder}
            qaId="user-details-avatar-image"
            className="rounded-full"
            style={{
              width: responsive.avatar.xlarge,
              height: responsive.avatar.xlarge,
              backgroundColor: theme.secondary,
              objectFit: 'cover',
            }}
          />
        </button>
        <div
          className="flex items-center justify-start"
          style={{ flex: 1, gap: responsive.gap['4xl'] }}
        >
          <UserDetailsStatColumn value={userData?.likes} label={l10n.common.likes} qa={{ id: 'user-details-stat-likes', value: userData?.likes ?? 0 }} />
          <UserDetailsStatColumn value={userData?.subscribers} label={l10n.common.subscribers} qa={{ id: 'user-details-stat-subscribers', value: userData?.subscribers ?? 0 }} />
          <UserDetailsStatColumn value={userData?.contentCount} label={l10n.common.videos} qa={{ id: 'user-details-stat-videos', value: userData?.contentCount ?? 0 }} />
        </div>
      </div>

      <div
        className="flex items-center"
        style={{
          color: theme.text.primary,
          fontSize: responsive.font.base,
          gap: responsive.gap.xs,
        }}
      >
        {isFreeSubscription ? (
          <span>{l10n.common.free}</span>
        ) : (
          <>
            <span>{subscriptionCoinsLabel}</span>
            <AssetIcon
              path="/assets/images/coins.png"
              width={responsive.icon.small}
              height={responsive.icon.small}
              color={theme.exclusive}
            />
          </>
        )}
        <span>{l10n.common.subscription}</span>
      </div>

      <ExpandableRichText
        description={userData?.bio}
        maxLines={2}
        onHashtagTap={(tag) => Navigator.navigateToContentTag(tag)}
        onMentionTap={(username) => Navigator.navigateToUserDetailsByMention(username)}
      />

      {userData && (
        <UserDetailsActions
          isCurrentUser={isCurrentUser}
          subscribeLabel={subscribeLabel}
          onSubscribePress={onSubscribePress}
          onCommunityPress={onCommunityPress}
          onSecondaryPress={onSecondaryPress}
        />
      )}
    </div>
  );
};
